import { Loader2, ShieldCheck, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AuthApiError } from "@/lib/auth";
import {
  listTrustedDevices,
  revokeTrustedDevice,
  type TrustedDevice,
} from "@/lib/auth/admin-api";
import { formatAbsoluteDateTime, formatRelativeTime } from "@/lib/format";

interface TrustedDevicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Best-effort browser/OS extraction from a UA string. Full-blown parsing is
// overkill here — admins/users mostly want to recognize their own device,
// and the raw UA is shown on hover as a fallback.
function summarizeUserAgent(ua: string | null): string {
  if (!ua) return "Unknown device";
  const browser =
    /Edg\//.test(ua)
      ? "Edge"
      : /OPR\//.test(ua)
        ? "Opera"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Firefox\//.test(ua)
            ? "Firefox"
            : /Safari\//.test(ua)
              ? "Safari"
              : null;
  const os = /Mac OS X|Macintosh/.test(ua)
    ? "macOS"
    : /Windows NT/.test(ua)
      ? "Windows"
      : /Android/.test(ua)
        ? "Android"
        : /iPhone|iPad|iOS/.test(ua)
          ? "iOS"
          : /Linux/.test(ua)
            ? "Linux"
            : null;
  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return os;
  return ua.slice(0, 40);
}

export function TrustedDevicesDialog({
  open,
  onOpenChange,
}: TrustedDevicesDialogProps) {
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchDevices = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(null);
    return listTrustedDevices(signal)
      .then((res) => {
        setDevices(res);
      })
      .catch((err: unknown) => {
        if (signal?.aborted) return;
        if (err instanceof AuthApiError) {
          setLoadError(err.serverMessage ?? "Couldn't load trusted devices.");
        } else {
          setLoadError("Couldn't reach the server. Check your connection.");
        }
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) {
      setRevokingId(null);
      setActionError(null);
      return;
    }
    const controller = new AbortController();
    void fetchDevices(controller.signal);
    return () => controller.abort();
  }, [open, fetchDevices]);

  const handleRevoke = async (device: TrustedDevice) => {
    if (revokingId) return;
    setRevokingId(device.id);
    setActionError(null);
    try {
      await revokeTrustedDevice(device.id);
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
    } catch (err) {
      if (err instanceof AuthApiError) {
        setActionError(err.serverMessage ?? "Couldn't revoke that device.");
      } else {
        setActionError("Couldn't reach the server. Check your connection.");
      }
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Trusted devices</DialogTitle>
          <DialogDescription>
            Devices that can skip the second-factor step at sign-in for 7 days
            after their last 2FA verification.
          </DialogDescription>
        </DialogHeader>

        {actionError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {actionError}
          </p>
        ) : null}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading devices…
          </div>
        ) : loadError ? (
          <div className="space-y-2 py-6 text-center">
            <p className="text-sm text-destructive">{loadError}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void fetchDevices()}
            >
              Retry
            </Button>
          </div>
        ) : devices.length === 0 ? (
          <div className="space-y-2 py-8 text-center">
            <ShieldCheck
              className="mx-auto h-5 w-5 text-muted-foreground"
              aria-hidden
            />
            <p className="text-sm text-muted-foreground">
              No trusted devices. You&apos;ll be asked for a 2FA code on every
              sign-in.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {devices.map((device) => {
              const summary = summarizeUserAgent(device.user_agent);
              const lastUsed = device.last_used_at
                ? formatRelativeTime(device.last_used_at)
                : formatRelativeTime(device.created_at);
              const lastUsedAbs = device.last_used_at
                ? formatAbsoluteDateTime(device.last_used_at)
                : formatAbsoluteDateTime(device.created_at);
              return (
                <li
                  key={device.id}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">
                        {summary}
                      </p>
                      {device.current ? (
                        <Badge variant="success">This device</Badge>
                      ) : null}
                    </div>
                    <p
                      className="truncate text-xs text-muted-foreground"
                      title={device.user_agent ?? undefined}
                    >
                      {device.last_ip ? (
                        <>
                          <span className="font-mono">{device.last_ip}</span> ·{" "}
                        </>
                      ) : null}
                      <span title={lastUsedAbs}>Last used {lastUsed}</span> ·
                      Expires {formatRelativeTime(device.expires_at)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRevoke(device)}
                    disabled={revokingId === device.id}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    {revokingId === device.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                    Revoke
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
